/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, useMediaQuery, useTheme, Button } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X, Menu } from "lucide-react";

import TransactionsTable, { TransactionRow } from "../components/transactions-table";
import Topbar from "../components/topbar";
import { DropdownOption } from "../components/ui/dropdown";
import { FilterParams } from "../components/transactions-table";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import transactionImage from '../files/images/transaction.png';
import TransactionDetail from "../components/popup/transaction-detail";
import TransactionDetailMobile from "../components/popup/mobile/transaction-detail-mobile";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";

import useApi from "../hooks/use-api";
import routes from "../api/routes";
import buildApiRequestConfig from "../api/url-builder";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { useSettings } from "../providers/settings-provider";

const defaultColumns = [
    { id: 'date', label: 'Date', width: 180 },
    { id: 'amount', label: 'Amount', width: 180 },
    { id: 'balance', label: 'Running Balance', width: 180 },
    { id: 'description', label: 'Description', width: 300 },
    { id: 'bank', label: 'Bank', width: 180 },
    { id: 'category', label: 'Category', width: 200 },
    { id: 'actions', label: 'Action', width: 100 },
];

export type ImportRow = {
    entityId?: number;
    date?: string;
    amount?: number;
    description?: string;
    categoryId?: number;
    bankId?: number;
    linkedTransactionId?: string;
};

export type FileDetails = {
    file?: File;
    parsedRows?: Record<string, string>[];
    failedRows?: Record<string, string>[];
    result: {
        successfulRows: number | 0;
        failedRows: number | 0;
        totalRows: number | 0;
    }
    headers?: string[];
};

const LinkedImports: React.FC = () => {
    const { user } = useUser();
    const { showError, showSuccess } = useToast();
    const { companySettings } = useSettings();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const TransactionDetailModal = isMobile ? TransactionDetailMobile : TransactionDetail;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    // Helper function to calculate start date from visibleHistory
    const calculateStartDateFromVisibleHistory = (settings?: any): Date => {
        let start = new Date();
        const visibleHistoryValue = settings?.settings?.visibleHistory || companySettings?.settings?.visibleHistory;
        
        if (visibleHistoryValue) {
            const match = visibleHistoryValue.match(/^(\d+)\s*(day|week|month|quarter|year|d|w|m|q|y)s?$/i);
            if (match) {
                const value = parseInt(match[1], 10);
                let unit = match[2].toLowerCase();

                // Normalize short forms to full unit names
                if (unit.length === 1) {
                    switch (unit) {
                        case 'd':
                            unit = 'day';
                            break;
                        case 'w':
                            unit = 'week';
                            break;
                        case 'm':
                            unit = 'month';
                            break;
                        case 'q':
                            unit = 'quarter';
                            break;
                        case 'y':
                            unit = 'year';
                            break;
                        default:
                            break;
                    }
                }

                start = new Date();
                switch (unit) {
                    case 'day':
                        start.setDate(start.getDate() - value);
                        break;
                    case 'week':
                        start.setDate(start.getDate() - value * 7);
                        break;
                    case 'month':
                        start.setMonth(start.getMonth() - value);
                        break;
                    case 'quarter':
                        start.setMonth(start.getMonth() - value * 3);
                        break;
                    case 'year':
                        start.setFullYear(start.getFullYear() - value);
                        break;
                    default:
                        break;
                }
            } else {
                // Default to 7 days if visibleHistory is not in expected format
                start = new Date();
                start.setDate(start.getDate() - 7);
            }
        } else {
            // Default to 7 days if visibleHistory is not set
            start = new Date();
            start.setDate(start.getDate() - 7);
        }
        return start;
    };

    // Helper function to calculate end date - returns future date by 30 days
    const calculateEndDateFromUnitValue = (): Date => {
        const now = new Date();
        now.setDate(now.getDate() + 30);
        return now;
    };
    
    const [startDate] = React.useState<Date>(() => {
        return calculateStartDateFromVisibleHistory();
    });
    const [endDate] = React.useState<Date>(() => {
        return calculateEndDateFromUnitValue();
    });
    const [columns, setColumns] = React.useState(defaultColumns);
    const [filterConfig, setFilterConfig] = React.useState<FilterParams | undefined>();
    const [companyStatus, setCompanyStatus] = React.useState<{ 
        currentBalance?: number, 
        firstNegativeDate?: Date, 
        firstNegativeTransaction?: {
            balance?: number,
            date?: Date,
            guid?: string,
        },
        wentNegative?: boolean,
        highestAmount?: number,
        lowestAmount?: number,
        latestTransaction?: Date | null,
        earliestTransaction?: Date | null,
        // New API fields
        startOfRecords?: { guid?: string | null; date?: Date | string | null } | null;
        endOfHorizon?: Date | string | null;
        totalCount?: number,
    }>({});
    
    const [banks, setBanks] = React.useState<DropdownOption[]>([]);
    

    const flatCategoryDropdownOptions = React.useMemo(() => {
        const flatten = (cats: any[]): DropdownOption[] => {
            const result: DropdownOption[] = [];
            cats.forEach((cat: any) => {
                result.push({ value: cat.id.toString(), label: cat.name });
                if (cat.children && cat.children.length > 0) {
                    result.push(...flatten(cat.children));
                }
            });
            return result;
        };
        return flatten([]);
    }, []);
    
    const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
    const [importId, setImportId] = React.useState<string | null>(null);
    const [data, setData] = React.useState([] as TransactionRow[]);
    const [openModal, setOpenModal] = React.useState('');

    const [fetchBanks, setFetchBanks] = useApi();

    const [fetch, setFetch] = useApi();
    const [deleteTransaction, setDeleteTransaction] = useApi();
    
    const [stage, setStage] = React.useState<'initialize' | 'fetching' | 'complete'>('initialize');

    // Read importId from URL query parameter
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const id = searchParams.get('importId');
        setImportId(id);
    }, [searchParams]);

    const handleFilterConfigChange = React.useCallback((value: React.SetStateAction<FilterParams | undefined>) => {
        let newFilterConfig: FilterParams | undefined;
        if (typeof value === 'function') {
            newFilterConfig = value(filterConfig);
        } else {
            newFilterConfig = value;
        }
        
        setFilterConfig(newFilterConfig);
    }, [filterConfig]);

    useEffect(() => {
        document.title = "FineAnts | Linked Imports";
        const { url: bankUrl, method: bankMethod } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchBanks({ url: bankUrl, method: bankMethod });
    }, [user]);    

    useEffect(() => {
        const params = [
            { param: 'entityId', value: user?.entityId }
        ];
        const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);
        setFetch({ url, method, body: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            viewBy: 'transactions',
            filterConfig: filterConfig ? JSON.stringify(filterConfig) : undefined,
            importId: importId || undefined,
            // This page needs every matching transaction, not a page of them -
            // explicit like the other non-paginated callers of this endpoint
            // (budget-actual.tsx, forecast.tsx, gst.tsx), so it isn't at the
            // mercy of the default pageSize once lazy loading is enabled.
            pageSize: 99999,
        } });

        setColumns(defaultColumns);
    }, [user, startDate, endDate, filterConfig, deleteTransaction, importId]);

    useEffect(() => {
        const { data, isLoading, error } = fetchBanks;
        if (data && !isLoading) {
            setBanks(data.map((bank: any) => ({ value: bank.id, label: `${bank.bank} (${bank.name})` })));
            setStage('fetching');
        } 
        if (error) {
            showError(error ? error.toString() : "Failed to fetch banks");
        }
    }, [fetchBanks]);

    useEffect(() => {
        const { data, isLoading, error } = deleteTransaction;
        if (data && !isLoading) {
            showSuccess("Transaction deleted successfully");
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to delete transaction");
        }
    }, [deleteTransaction]);

    useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (!isLoading) {
            const transactions = data?.transactions ? data.transactions.map((tx: any) => ({
                id: tx.guid,
                date: tx.date,
                amount: tx.amount,
                balance: tx.balance,
                description: tx.description,
                bank: tx.bankAccountId || 'No Bank Account',
                type: tx.type,
                category: tx.categoryId || '',
                provisionId: tx.provisionTemplateId || null,
                invoiceNumber: tx.invoiceNumber || '',
                isRecurring: tx.isRecurring,
                linkedTransactionId: tx.linkedTransactionId || null,
            })) : [];
            setData(transactions);
            setStage('complete');
            const status = data?.status || {};
            // Convert date strings to Date objects
            if (status.earliestTransaction && typeof status.earliestTransaction === 'string') {
                status.earliestTransaction = new Date(status.earliestTransaction);
            }
            if (status.latestTransaction && typeof status.latestTransaction === 'string') {
                status.latestTransaction = new Date(status.latestTransaction);
            }
            // Parse new API fields when present
            if (status.startOfRecords && typeof status.startOfRecords.date === 'string') {
                status.startOfRecords.date = new Date(status.startOfRecords.date);
            }
            if (status.endOfHorizon && typeof status.endOfHorizon === 'string') {
                status.endOfHorizon = new Date(status.endOfHorizon);
            }
            setCompanyStatus(status);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch transactions");
        }
    }, [fetch]);

    const handleGroupRowClick = (id: string) => {
        setSelectedTransactionId(id);
        setOpenModal('detail');
    };

    return (
        <div style={{ width: '100%' }}>
            <TransactionDetailModal
                open={openModal === 'detail'}
                transactionId={selectedTransactionId}
                onClose={() => {setOpenModal(''); setSelectedTransactionId(null);}}
                onLink={() => {}}
                companyStatus={{
                    highestAmount: companyStatus.highestAmount || 9999,
                    lowestAmount: companyStatus.lowestAmount || -9999,
                }}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Transaction?"
                description="This action will permanently remove the selected transaction."
                image={{ src: transactionImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    const { url, method } = buildApiRequestConfig(routes.transaction.delete, [{ param: 'guid', value: selectedTransactionId || '' }]);
                    setDeleteTransaction({ url, method });
                }}
            />
            <Topbar
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box display="flex" justifyContent="flex-start" alignItems="center" width="100%" pr={1} gap={1}>
                    { importId &&
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: 16,
                                fontWeight: 500,
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                color: '#00796b',
                                border: '1px solid var(--color-primary)',
                            }}
                        >
                            Viewing transactions from Import ID: <b>{importId.padStart(3, '0')}</b>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    navigate(`/settings/imports?importId=${importId}`, { replace: true });
                                }}
                                sx={{
                                    padding: '2px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 182, 155, 0.1)'
                                    }
                                }}
                            >
                                <X size={18} color="red"/>
                            </IconButton>
                        </Box>
                    }
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <TransactionsTable 
                    data={data} 
                    columns={columns} 
                    onDelete={(row) => {
                        setSelectedTransactionId(row.id);
                        setOpenModal('delete');
                    }}
                    onEdit={() => {}}
                    setColumnFilter={handleFilterConfigChange} 
                    columnFilter={filterConfig}
                    bankOptions={banks}
                    categoryOptions={flatCategoryDropdownOptions}
                    hierarchicalCategories={[]}
                    onRowClick={handleGroupRowClick}
                    companyStatus={{
                        highestAmount: companyStatus.highestAmount,
                        lowestAmount: companyStatus.lowestAmount,
                        latestTransaction: companyStatus.latestTransaction,
                        earliestTransaction: companyStatus.earliestTransaction,
                        startOfRecords: companyStatus.startOfRecords,
                        endOfHorizon: companyStatus.endOfHorizon,
                        totalCount: companyStatus.totalCount,
                    }}
                    stage={stage}
                />
            </div>
            <SidebarMobileDrawer 
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
        </div>
    );
};




export default LinkedImports;